/**
 * Pure math behind the camera player's gestures.
 *
 * The desktop player pans by dragging with the mouse and zooms with the wheel;
 * a phone has no wheel, so a pinch and a one-finger drag stand in for it. The
 * two input families must end up sending the same PTZ commands and producing
 * the same digital-zoom transform, so the arithmetic lives here — no DOM, no
 * Vue, testable on its own (`cameraGestures.test.ts`).
 */

export interface Vec2 {
  x: number;
  y: number;
}

export interface TouchPoint {
  clientX: number;
  clientY: number;
}

/** How far (px) a pointer may travel and still count as a tap rather than a drag. */
export const TAP_SLOP_PX = 8;

/**
 * Velocity for one pan/tilt sample, in the camera's ONVIF space (-1..1).
 *
 * `gain` converts a fraction of the viewport into full speed: at gain 6, a drag
 * of one sixth of the video width is full speed. X is inverted — dragging right
 * moves the image right, which means the camera turns left.
 */
export function ptzPanVelocity(dx: number, dy: number, width: number, height: number, gain = 6): Vec2 {
  const clamp = (value: number) => Math.max(-1, Math.min(1, value));
  return {
    x: -clamp(width > 0 ? (dx / width) * gain : 0),
    y: clamp(height > 0 ? (dy / height) * gain : 0),
  };
}

/** True while a gesture is still a tap (a click-to-center, not a drag). */
export function isTap(dx: number, dy: number, slop = TAP_SLOP_PX): boolean {
  return Math.sqrt(dx * dx + dy * dy) < slop;
}

/**
 * Zoom velocity (-1, 0, 1) for a pinch, from the change in finger distance since
 * the previous frame.
 *
 * The dead band keeps a resting pinch (and the small jitter of fingers held
 * still) from streaming zoom commands at the camera.
 */
export function ptzZoomVelocity(ratio: number, previousRatio: number, deadBand = 0.04): number {
  if (!Number.isFinite(ratio) || !Number.isFinite(previousRatio)) return 0;
  const delta = ratio - previousRatio;
  if (Math.abs(delta) < deadBand) return 0;
  return delta > 0 ? 1 : -1;
}

/** Distance and midpoint of two touch points — the pinch pair. */
export function touchPair(a: TouchPoint, b: TouchPoint): { distance: number; midX: number; midY: number } {
  const dx = b.clientX - a.clientX;
  const dy = b.clientY - a.clientY;
  return {
    distance: Math.sqrt(dx * dx + dy * dy),
    midX: (a.clientX + b.clientX) / 2,
    midY: (a.clientY + b.clientY) / 2,
  };
}

export interface PinchTransformInput {
  /** Current pinch midpoint, in client coordinates. */
  midX: number;
  midY: number;
  /** Container box (viewport coordinates). */
  rectLeft: number;
  rectTop: number;
  /** Scale and offset when the pinch started. */
  startScale: number;
  startOffsetX: number;
  startOffsetY: number;
  /** Midpoint when the pinch started. */
  startMidX: number;
  startMidY: number;
  /** Finger distance when the pinch started, and now. */
  startDistance: number;
  distance: number;
  minScale: number;
  maxScale: number;
}

export interface PinchTransform {
  scale: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Digital-zoom transform for a pinch: the content point that sat under the
 * fingers when the pinch began stays under them as they spread.
 *
 * Because the anchor is re-projected onto the *current* midpoint, a two-finger
 * drag pans as a side effect — the same thing desktop users get by dragging a
 * zoomed image.
 */
export function pinchTransform(input: PinchTransformInput): PinchTransform {
  const {
    midX,
    midY,
    rectLeft,
    rectTop,
    startScale,
    startOffsetX,
    startOffsetY,
    startMidX,
    startMidY,
    startDistance,
    distance,
    minScale,
    maxScale,
  } = input;

  // Content-space point under the fingers when the pinch began.
  const anchorX = (startMidX - rectLeft - startOffsetX) / startScale;
  const anchorY = (startMidY - rectTop - startOffsetY) / startScale;

  const ratio = startDistance > 0 ? distance / startDistance : 1;
  const scale = Math.max(minScale, Math.min(maxScale, startScale * ratio));

  return {
    scale,
    offsetX: midX - rectLeft - anchorX * scale,
    offsetY: midY - rectTop - anchorY * scale,
  };
}
